import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", email: "", role: "editor", password: "" };

export default function AdminUsers() {
  const { user } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data.results));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Account created");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  const resetPassword = (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 characters):`);
    if (pw) patch(u.id, { password: pw }, "Password reset");
  };

  const isProtected = (u) => u.role === "administrator" || u.id === user.id;

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-navy">Team accounts</h1>
      <p className="mt-1 text-sm text-[var(--mineral)]">Create and manage editor and reviewer logins. The administrator account is managed via environment configuration.</p>

      <div className="mt-6 grid gap-8 lg:grid-cols-[360px_1fr]">
        <form onSubmit={create} className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6" data-testid="create-user-form">
          <p className="eyebrow mb-4 flex items-center gap-2"><UserPlus size={14} /> New account</p>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-navy">Full name</span>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="new-user-name" required className="w-full rounded-xl border border-[var(--line)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cobalt)]" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-navy">Email</span>
              <input type="email" autoComplete="off" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-user-email" required className="w-full rounded-xl border border-[var(--line)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cobalt)]" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-navy">Role</span>
              <select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="new-user-role" className="w-full rounded-xl border border-[var(--line)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cobalt)]">
                <option value="editor">Editor — manage &amp; publish content</option>
                <option value="reviewer">Reviewer — review submissions</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-navy">Temporary password</span>
              <input type="text" autoComplete="new-password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="new-user-password" required minLength={8} placeholder="min 8 characters" className="w-full rounded-xl border border-[var(--line)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cobalt)]" />
            </label>
            <button type="submit" disabled={busy} data-testid="create-user-submit" className="btn-transition w-full rounded-full bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white hover:bg-[var(--cobalt)] disabled:opacity-60">
              {busy ? "Creating…" : "Create account"}
            </button>
          </div>
        </form>

        <div className="overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--paper)]">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--line)] bg-[var(--ivory)] text-left">
              <tr>
                <th className="p-4 font-medium text-navy">Name</th>
                <th className="p-4 font-medium text-navy">Role</th>
                <th className="p-4 font-medium text-navy">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[var(--line)] last:border-0" data-testid={`user-row-${u.email}`}>
                  <td className="p-4">
                    <div className="font-medium text-navy">{u.name}</div>
                    <div className="font-mono text-xs text-[var(--mineral)]">{u.email}</div>
                  </td>
                  <td className="p-4">
                    {isProtected(u) ? (
                      <span className="font-mono text-xs uppercase text-[var(--cobalt)]">{u.role}</span>
                    ) : (
                      <select value={u.role} onChange={(e) => patch(u.id, { role: e.target.value }, "Role updated")} data-testid={`role-select-${u.email}`} className="rounded-full border border-[var(--line)] px-2.5 py-1 text-xs outline-none focus:border-[var(--cobalt)]">
                        <option value="editor">editor</option>
                        <option value="reviewer">reviewer</option>
                      </select>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`rounded-full px-2.5 py-1 font-mono text-xs ${u.active ? "bg-[var(--success)]/15 text-[var(--success)]" : "bg-[var(--error)]/15 text-[var(--error)]"}`}>
                      {u.active ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    {!isProtected(u) && (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => resetPassword(u)} data-testid={`reset-pw-${u.email}`} className="rounded-full border border-[var(--line)] px-3 py-1.5 text-xs font-semibold text-navy hover:border-[var(--cobalt)]">Reset password</button>
                        <button onClick={() => patch(u.id, { active: !u.active }, u.active ? "Deactivated" : "Reactivated")} data-testid={`toggle-active-${u.email}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold text-white ${u.active ? "bg-[var(--error)]" : "bg-[var(--success)]"}`}>
                          {u.active ? "Deactivate" : "Reactivate"}
                        </button>
                      </div>
                    )}
                    {isProtected(u) && <span className="font-mono text-xs text-[var(--mineral)]">{u.id === user.id ? "You" : "Protected"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
